import { useState, useRef, useCallback } from 'react'
import { apiUrl, authFetch } from '../../lib/api'
import { SaveDesignModal, UseInProjectModal } from './UseInProjectModal'

interface ComponentNode {
  id: string
  type: string
  props: Record<string, any>
  children: ComponentNode[]
}

const COMPONENT_LIBRARY = [
  { type: 'div', label: 'Container', icon: '▢', category: 'layout' },
  { type: 'flex', label: 'Flex Row', icon: '⇔', category: 'layout' },
  { type: 'grid', label: 'Grid', icon: '⊞', category: 'layout' },
  { type: 'section', label: 'Section', icon: '§', category: 'layout' },
  { type: 'heading', label: 'Heading', icon: 'H', category: 'text' },
  { type: 'text', label: 'Text', icon: 'T', category: 'text' },
  { type: 'button', label: 'Button', icon: '⬡', category: 'input' },
  { type: 'input', label: 'Input', icon: '▭', category: 'input' },
  { type: 'textarea', label: 'Textarea', icon: '▯', category: 'input' },
  { type: 'image', label: 'Image', icon: '🖼', category: 'media' },
  { type: 'link', label: 'Link', icon: '🔗', category: 'text' },
  { type: 'list', label: 'List', icon: '≡', category: 'text' },
  { type: 'card', label: 'Card', icon: '▣', category: 'layout' },
  { type: 'navbar', label: 'Navbar', icon: '▬', category: 'layout' },
  { type: 'hero', label: 'Hero', icon: '★', category: 'layout' },
  { type: 'footer', label: 'Footer', icon: '▁', category: 'layout' },
  { type: 'form', label: 'Form', icon: '📋', category: 'input' },
  { type: 'table', label: 'Table', icon: '⊞', category: 'data' },
  { type: 'badge', label: 'Badge', icon: '●', category: 'text' },
  { type: 'avatar', label: 'Avatar', icon: '◉', category: 'media' },
]

const DEFAULT_PROPS: Record<string, Record<string, any>> = {
  div: { padding: '16px', background: '#1a1a2e' },
  flex: { display: 'flex', gap: '12px', alignItems: 'center', padding: '12px' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', padding: '12px' },
  section: { padding: '32px 24px', background: '#16213e' },
  heading: { text: 'Heading', fontSize: '24px', fontWeight: '700', color: '#fff' },
  text: { text: 'Lorem ipsum dolor sit amet', fontSize: '14px', color: '#ccc' },
  button: { text: 'Click me', padding: '10px 20px', background: '#7c6ef5', color: '#fff', borderRadius: '8px', border: 'none', cursor: 'pointer' },
  input: { placeholder: 'Enter text...', padding: '10px 14px', borderRadius: '6px', border: '1px solid #333', background: '#0f0f1a', color: '#fff', width: '100%' },
  textarea: { placeholder: 'Enter text...', padding: '10px 14px', borderRadius: '6px', border: '1px solid #333', background: '#0f0f1a', color: '#fff', width: '100%', rows: 4 },
  image: { src: 'https://placehold.co/300x200/1a1a2e/7c6ef5?text=Image', width: '100%', borderRadius: '8px' },
  link: { text: 'Click here', color: '#7c6ef5', href: '#' },
  list: { items: ['Item 1', 'Item 2', 'Item 3'], color: '#ccc' },
  card: { padding: '20px', background: '#1a1a2e', borderRadius: '12px', border: '1px solid #333' },
  navbar: { background: '#0f0f1a', padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  hero: { padding: '60px 24px', textAlign: 'center', background: 'linear-gradient(135deg, #1a1a2e, #16213e)' },
  footer: { padding: '24px', background: '#0a0a14', textAlign: 'center', color: '#666' },
  form: { display: 'flex', flexDirection: 'column', gap: '12px', padding: '20px' },
  table: { width: '100%', borderCollapse: 'collapse' },
  badge: { text: 'Badge', padding: '4px 10px', borderRadius: '20px', background: '#7c6ef5', color: '#fff', fontSize: '11px', display: 'inline-block' },
  avatar: { width: '40px', height: '40px', borderRadius: '50%', background: '#7c6ef5' },
}

export function VisualBuilderPanel({ onClose }: { onClose: () => void }) {
  const [nodes, setNodes] = useState<ComponentNode[]>([])
  const [selected, setSelected] = useState<string | null>(null)
  const [view, setView] = useState<'visual' | 'code'>('visual')
  const [codeTab, setCodeTab] = useState<'preview' | 'source'>('preview')
  const [generatedCode, setGeneratedCode] = useState('')
  const [generating, setGenerating] = useState(false)
  const [showImport, setShowImport] = useState(false)
  const [importCode, setImportCode] = useState('')
  const [showSave, setShowSave] = useState(false)
  const [showUseInProject, setShowUseInProject] = useState(false)
  const dragType = useRef<string | null>(null)

  const addNode = (type: string, parentId?: string) => {
    const node: ComponentNode = { id: crypto.randomUUID(), type, props: { ...DEFAULT_PROPS[type] }, children: [] }
    if (parentId) {
      setNodes(prev => addToParent(prev, parentId, node))
    } else {
      setNodes(prev => [...prev, node])
    }
    setSelected(node.id)
  }

  const handleImport = () => {
    if (!importCode.trim()) return
    const parsed = parseHTMLToNodes(importCode)
    if (parsed.length > 0) {
      setNodes(parsed)
      setShowImport(false)
      setImportCode('')
      setView('visual')
    }
  }

  const addToParent = (nodes: ComponentNode[], parentId: string, child: ComponentNode): ComponentNode[] => {
    return nodes.map(n => {
      if (n.id === parentId) return { ...n, children: [...n.children, child] }
      return { ...n, children: addToParent(n.children, parentId, child) }
    })
  }

  const removeNode = (id: string) => {
    setNodes(prev => filterNodes(prev, id))
    if (selected === id) setSelected(null)
  }

  const filterNodes = (nodes: ComponentNode[], id: string): ComponentNode[] => {
    return nodes.filter(n => n.id !== id).map(n => ({ ...n, children: filterNodes(n.children, id) }))
  }

  const updateProps = (id: string, props: Record<string, any>) => {
    setNodes(prev => updateNodeProps(prev, id, props))
  }

  const updateNodeProps = (nodes: ComponentNode[], id: string, props: Record<string, any>): ComponentNode[] => {
    return nodes.map(n => {
      if (n.id === id) return { ...n, props: { ...n.props, ...props } }
      return { ...n, children: updateNodeProps(n.children, id, props) }
    })
  }

  const findNode = (nodes: ComponentNode[], id: string): ComponentNode | null => {
    for (const n of nodes) {
      if (n.id === id) return n
      const found = findNode(n.children, id)
      if (found) return found
    }
    return null
  }

  const handleDrop = (e: React.DragEvent, parentId?: string) => {
    e.preventDefault()
    e.stopPropagation()
    if (dragType.current) {
      addNode(dragType.current, parentId)
      dragType.current = null
    }
  }

  const generateCode = useCallback(async () => {
    setGenerating(true)
    setView('code')
    try {
      const res = await authFetch(apiUrl('/api/chat/stream'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: [{ role: 'user', content: `Convert this component layout into a single, complete, beautiful HTML page with inline CSS. Make it look professional and polished. Do NOT use React, Tailwind, or any framework — just pure HTML and CSS.

Return ONLY the complete HTML (starting with <!DOCTYPE html>) — no explanation, no markdown, no code fences.

Component tree:
${JSON.stringify(nodes, null, 2)}` }],
          context: { mode: 'ask', model: 'gemini-2.5-flash' },
        }),
      })
      if (!res.body) return
      const reader = res.body.getReader()
      const dec = new TextDecoder()
      let text = ''
      let buf = ''
      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buf += dec.decode(value, { stream: true })
        const lines = buf.split('\n'); buf = lines.pop() ?? ''
        for (const line of lines) {
          if (!line.trim().startsWith('data: ')) continue
          try {
            const data = JSON.parse(line.trim().slice(6))
            if (data.text) text += data.text
          } catch {}
        }
      }
      // Clean up — remove markdown fences if AI wrapped it
      text = text.replace(/^```html\n?/i, '').replace(/```$/m, '').trim()
      setGeneratedCode(text)
    } catch {} finally { setGenerating(false) }
  }, [nodes])

  const saveToFile = useCallback(async () => {
    const html = wrapInHTML(generatedCode ? extractHTMLFromCode(generatedCode) : nodesToFullHTML(nodes))
    try {
      await authFetch(apiUrl('/api/files/write'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ path: 'visual-builder/index.html', content: html }),
      })
      window.dispatchEvent(new CustomEvent('pulse:files-changed'))
      alert('Saved to visual-builder/index.html')
    } catch {}
  }, [nodes, generatedCode])

  const selectedNode = selected ? findNode(nodes, selected) : null

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 999, background: 'var(--bg-0)', display: 'flex', flexDirection: 'column' }}>
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg-1)', flexShrink: 0 }}>
        <span style={{ fontFamily: 'var(--font-display)', fontWeight: 900, fontSize: 13, color: 'var(--text-0)' }}>🎨 Visual Builder</span>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', gap: 4 }}>
          <button onClick={() => setView('visual')} style={tabBtn(view === 'visual')}>Visual</button>
          <button onClick={() => setView('code')} style={tabBtn(view === 'code')}>Code</button>
        </div>
        <button onClick={generateCode} disabled={generating || nodes.length === 0} style={{ padding: '4px 12px', borderRadius: 6, border: '1px solid var(--accent)', background: 'rgba(124,110,245,0.1)', color: 'var(--accent)', cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700 }}>
          {generating ? 'Generating...' : '⚡ Generate Code'}
        </button>
        <button onClick={() => setShowSave(true)} disabled={nodes.length === 0 && !generatedCode} style={{ padding: '4px 12px', borderRadius: 6, border: '1px solid var(--accent-2)', background: 'rgba(95,212,164,0.1)', color: 'var(--accent-2)', cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700 }}>
          💾 Save
        </button>
        <button onClick={() => setShowUseInProject(true)} disabled={nodes.length === 0 && !generatedCode} style={{ padding: '4px 12px', borderRadius: 6, border: '1px solid var(--accent-3)', background: 'rgba(245,161,87,0.1)', color: 'var(--accent-3)', cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700 }}>
          📂 Use in Project
        </button>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer', fontSize: 16 }}>✕</button>
      </div>

      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {view === 'visual' ? (
          <>
            {/* Component Library */}
            <div style={{ width: 180, borderRight: '1px solid var(--border)', overflow: 'auto', padding: '8px', flexShrink: 0 }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', letterSpacing: '0.1em', marginBottom: 8 }}>COMPONENTS</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                {COMPONENT_LIBRARY.map(c => (
                  <div
                    key={c.type}
                    draggable
                    onDragStart={() => { dragType.current = c.type }}
                    style={{ padding: '8px 4px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-2)', cursor: 'grab', textAlign: 'center', fontSize: 9, fontFamily: 'var(--font-mono)', color: 'var(--text-1)', transition: 'border-color 0.12s' }}
                    onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--accent)' }}
                    onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--border)' }}
                  >
                    <div style={{ fontSize: 16, marginBottom: 2 }}>{c.icon}</div>
                    {c.label}
                  </div>
                ))}
              </div>
            </div>

            {/* Canvas */}
            <div
              style={{ flex: 1, overflow: 'auto', padding: 20, background: '#0a0a14' }}
              onDragOver={e => e.preventDefault()}
              onDrop={e => handleDrop(e)}
            >
              {nodes.length === 0 && (
                <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-2)', fontFamily: 'var(--font-mono)', fontSize: 12, border: '2px dashed var(--border)', borderRadius: 12 }}>
                  Drag components here to start building
                </div>
              )}
              {nodes.map(node => (
                <CanvasNode key={node.id} node={node} selected={selected} onSelect={setSelected} onDrop={handleDrop} />
              ))}
            </div>

            {/* Properties Panel */}
            <div style={{ width: 220, borderLeft: '1px solid var(--border)', overflow: 'auto', padding: 10, flexShrink: 0 }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', letterSpacing: '0.1em', marginBottom: 8 }}>PROPERTIES</div>
              {selectedNode ? (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', fontFamily: 'var(--font-mono)', marginBottom: 8 }}>{selectedNode.type}</div>
                  {Object.entries(selectedNode.props).map(([key, val]) => (
                    <div key={key} style={{ marginBottom: 6 }}>
                      <label style={{ fontSize: 9, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', display: 'block', marginBottom: 2 }}>{key}</label>
                      <input
                        value={String(val)}
                        onChange={e => updateProps(selectedNode.id, { [key]: e.target.value })}
                        style={{ width: '100%', padding: '4px 6px', borderRadius: 4, border: '1px solid var(--border-2)', background: 'var(--bg-2)', color: 'var(--text-0)', fontFamily: 'var(--font-mono)', fontSize: 10, outline: 'none' }}
                      />
                    </div>
                  ))}
                  <button onClick={() => removeNode(selectedNode.id)} style={{ marginTop: 8, width: '100%', padding: 6, borderRadius: 4, border: '1px solid var(--red)', background: 'rgba(240,112,112,0.08)', color: 'var(--red)', cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 10 }}>Delete</button>
                </div>
              ) : (
                <div style={{ color: 'var(--text-2)', fontSize: 10, fontFamily: 'var(--font-mono)' }}>Select a component to edit its properties</div>
              )}
            </div>
          </>
        ) : (
          /* Code View */
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Sub-tabs: Preview | Source */}
            {generatedCode && (
              <div style={{ display: 'flex', gap: 4, padding: '8px 16px', borderBottom: '1px solid var(--border)', flexShrink: 0, alignItems: 'center' }}>
                <button onClick={() => setCodeTab('preview')} style={tabBtn(codeTab === 'preview')}>Preview</button>
                <button onClick={() => setCodeTab('source')} style={tabBtn(codeTab === 'source')}>Source</button>
                <div style={{ flex: 1 }} />
                <button onClick={() => { navigator.clipboard.writeText(generatedCode) }} style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid var(--border-2)', background: 'none', color: 'var(--text-1)', cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 10 }}>📋 Copy</button>
                <button onClick={saveToFile} style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid var(--accent-2)', background: 'rgba(95,212,164,0.1)', color: 'var(--accent-2)', cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700 }}>💾 Save</button>
              </div>
            )}

            {/* Content */}
            {!generatedCode && !generating && (
              <div style={{ flex: 1, overflow: 'auto', padding: 16, background: 'var(--bg-0)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', letterSpacing: '0.1em' }}>CANVAS HTML</div>
                  <div style={{ flex: 1 }} />
                  <button onClick={() => setShowImport(true)} style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid var(--accent)', background: 'rgba(124,110,245,0.1)', color: 'var(--accent)', cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700 }}>📥 Import HTML</button>
                </div>
                {nodes.length > 0 ? (
                  <pre style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--accent-2)', lineHeight: 1.6, whiteSpace: 'pre-wrap', background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: 8, padding: 12 }}>
                    {nodesToHTML(nodes)}
                  </pre>
                ) : (
                  <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                    <p style={{ marginBottom: 12 }}>No components yet. You can:</p>
                    <p>• Drag components in the <strong>Visual</strong> tab</p>
                    <p>• Or click <strong>📥 Import HTML</strong> to start from code</p>
                  </div>
                )}

                {/* Import Modal */}
                {showImport && (
                  <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowImport(false)}>
                    <div onClick={e => e.stopPropagation()} style={{ width: 500, maxWidth: '90vw', maxHeight: '70vh', background: 'var(--bg-1)', border: '1px solid var(--border-2)', borderRadius: 14, padding: 20, display: 'flex', flexDirection: 'column', boxShadow: '0 16px 48px rgba(0,0,0,0.5)' }}>
                      <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 14, color: 'var(--text-0)', marginBottom: 8 }}>📥 Import HTML</div>
                      <div style={{ fontSize: 10, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', marginBottom: 12 }}>Paste your HTML code below. It will be loaded into the visual builder.</div>
                      <textarea
                        value={importCode}
                        onChange={e => setImportCode(e.target.value)}
                        placeholder='<div style="padding: 16px;">\n  <h1>Hello World</h1>\n  <button>Click me</button>\n</div>'
                        style={{ flex: 1, minHeight: 200, padding: 12, borderRadius: 8, border: '1px solid var(--border-2)', background: 'var(--bg-0)', color: 'var(--accent-2)', fontFamily: 'var(--font-mono)', fontSize: 11, outline: 'none', resize: 'vertical', lineHeight: 1.5 }}
                      />
                      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                        <button onClick={() => setShowImport(false)} style={{ flex: 1, padding: 8, borderRadius: 6, border: '1px solid var(--border-2)', background: 'none', color: 'var(--text-2)', cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 11 }}>Cancel</button>
                        <button onClick={handleImport} style={{ flex: 1, padding: 8, borderRadius: 6, border: 'none', background: 'var(--accent)', color: '#fff', cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700 }}>Import & Build</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {generating && (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)', fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                <span style={{ animation: 'pulse-heartbeat 1.4s infinite', marginRight: 8 }}>⚡</span> Generating polished design...
              </div>
            )}

            {generatedCode && codeTab === 'preview' && (
              <div style={{ flex: 1, overflow: 'hidden' }}>
                <iframe
                  srcDoc={generatedCode}
                  style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }}
                  sandbox="allow-scripts"
                />
              </div>
            )}

            {generatedCode && codeTab === 'source' && (
              <div style={{ flex: 1, overflow: 'auto', padding: 16, background: 'var(--bg-0)' }}>
                <pre style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--accent-2)', lineHeight: 1.6, whiteSpace: 'pre-wrap', background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: 8, padding: 12 }}>
                  {generatedCode}
                </pre>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      {showSave && <SaveDesignModal html={generatedCode || wrapInHTML(nodesToFullHTML(nodes))} onClose={() => setShowSave(false)} onSaved={() => {}} />}
      {showUseInProject && <UseInProjectModal html={generatedCode || wrapInHTML(nodesToFullHTML(nodes))} onClose={() => setShowUseInProject(false)} />}
    </div>
  )
}

function CanvasNode({ node, selected, onSelect, onDrop }: { node: ComponentNode; selected: string | null; onSelect: (id: string) => void; onDrop: (e: React.DragEvent, parentId?: string) => void }) {
  const isSelected = selected === node.id
  const isContainer = ['div', 'flex', 'grid', 'section', 'card', 'navbar', 'hero', 'footer', 'form'].includes(node.type)

  const style: React.CSSProperties = {
    ...extractStyles(node.props),
    outline: isSelected ? '2px solid var(--accent)' : '1px dashed rgba(255,255,255,0.1)',
    outlineOffset: 2,
    cursor: 'pointer',
    minHeight: isContainer ? 40 : undefined,
    position: 'relative',
    transition: 'outline 0.12s',
  }

  const content = renderContent(node)

  return (
    <div
      style={style}
      onClick={e => { e.stopPropagation(); onSelect(node.id) }}
      onDragOver={isContainer ? e => e.preventDefault() : undefined}
      onDrop={isContainer ? e => onDrop(e, node.id) : undefined}
    >
      {content}
      {node.children.map(child => (
        <CanvasNode key={child.id} node={child} selected={selected} onSelect={onSelect} onDrop={onDrop} />
      ))}
      {isSelected && <span style={{ position: 'absolute', top: -8, left: 4, fontSize: 8, background: 'var(--accent)', color: '#fff', padding: '1px 4px', borderRadius: 3, fontFamily: 'var(--font-mono)' }}>{node.type}</span>}
    </div>
  )
}

function renderContent(node: ComponentNode): React.ReactNode {
  switch (node.type) {
    case 'heading': return <h2 style={{ margin: 0, fontSize: node.props.fontSize, fontWeight: node.props.fontWeight, color: node.props.color }}>{node.props.text}</h2>
    case 'text': return <p style={{ margin: 0, fontSize: node.props.fontSize, color: node.props.color }}>{node.props.text}</p>
    case 'button': return <button style={{ padding: node.props.padding, background: node.props.background, color: node.props.color, borderRadius: node.props.borderRadius, border: node.props.border, cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 12 }}>{node.props.text}</button>
    case 'input': return <input placeholder={node.props.placeholder} style={{ padding: node.props.padding, borderRadius: node.props.borderRadius, border: node.props.border, background: node.props.background, color: node.props.color, width: node.props.width }} readOnly />
    case 'textarea': return <textarea placeholder={node.props.placeholder} style={{ padding: node.props.padding, borderRadius: node.props.borderRadius, border: node.props.border, background: node.props.background, color: node.props.color, width: node.props.width }} rows={node.props.rows} readOnly />
    case 'image': return <img src={node.props.src} style={{ width: node.props.width, borderRadius: node.props.borderRadius }} alt="" />
    case 'link': return <a href="#" style={{ color: node.props.color, fontFamily: 'var(--font-mono)', fontSize: 12 }}>{node.props.text}</a>
    case 'list': return <ul style={{ margin: 0, paddingLeft: 20, color: node.props.color }}>{(node.props.items ?? []).map((item: string, i: number) => <li key={i} style={{ fontSize: 12 }}>{item}</li>)}</ul>
    case 'badge': return <span style={{ padding: node.props.padding, borderRadius: node.props.borderRadius, background: node.props.background, color: node.props.color, fontSize: node.props.fontSize, display: node.props.display }}>{node.props.text}</span>
    case 'avatar': return <div style={{ width: node.props.width, height: node.props.height, borderRadius: node.props.borderRadius, background: node.props.background }} />
    default: return null
  }
}

function extractStyles(props: Record<string, any>): React.CSSProperties {
  const styleKeys = ['padding', 'margin', 'background', 'display', 'gap', 'alignItems', 'justifyContent', 'flexDirection', 'gridTemplateColumns', 'borderRadius', 'border', 'textAlign', 'width', 'height']
  const styles: any = {}
  for (const key of styleKeys) {
    if (props[key]) styles[key] = props[key]
  }
  return styles
}

function nodesToHTML(nodes: ComponentNode[], indent = 0): string {
  const pad = '  '.repeat(indent)
  return nodes.map(n => {
    const tag = n.type === 'flex' || n.type === 'grid' || n.type === 'card' || n.type === 'section' || n.type === 'hero' || n.type === 'footer' || n.type === 'navbar' ? 'div' : n.type === 'heading' ? 'h2' : n.type === 'text' ? 'p' : n.type === 'link' ? 'a' : n.type === 'badge' ? 'span' : n.type === 'avatar' ? 'div' : n.type === 'image' ? 'img' : n.type
    const text = n.props.text ?? ''
    const styleStr = Object.entries(extractStyles(n.props)).map(([k, v]) => `${k.replace(/([A-Z])/g, '-$1').toLowerCase()}:${v}`).join(';')
    const attrs = styleStr ? ` style="${styleStr}"` : ''
    if (n.type === 'image') return `${pad}<img src="${n.props.src}" style="${styleStr}" alt="" />`
    if (n.type === 'input') return `${pad}<input placeholder="${n.props.placeholder}"${attrs} />`
    const children = n.children.length > 0 ? '\n' + nodesToHTML(n.children, indent + 1) + '\n' + pad : text
    return `${pad}<${tag}${attrs}>${children}</${tag}>`
  }).join('\n')
}

function nodesToFullHTML(nodes: ComponentNode[]): string {
  const body = nodes.map(n => nodeToHTMLElement(n)).join('\n')
  return body
}

function nodeToHTMLElement(n: ComponentNode): string {
  const tag = n.type === 'flex' || n.type === 'grid' || n.type === 'card' || n.type === 'section' || n.type === 'hero' || n.type === 'footer' || n.type === 'navbar' ? 'div' : n.type === 'heading' ? 'h2' : n.type === 'text' ? 'p' : n.type === 'link' ? 'a' : n.type === 'badge' ? 'span' : n.type === 'avatar' ? 'div' : n.type === 'image' ? 'img' : n.type
  const styles = extractStyles(n.props)
  const styleStr = Object.entries(styles).map(([k, v]) => `${k.replace(/([A-Z])/g, '-$1').toLowerCase()}:${v}`).join(';')
  const attrs = styleStr ? ` style="${styleStr}"` : ''

  if (n.type === 'image') return `<img src="${n.props.src}" style="${styleStr};max-width:100%" alt="" />`
  if (n.type === 'input') return `<input placeholder="${n.props.placeholder}"${attrs} />`
  if (n.type === 'textarea') return `<textarea placeholder="${n.props.placeholder}"${attrs} rows="${n.props.rows || 4}"></textarea>`

  const text = n.props.text ?? ''
  const childrenHTML = n.children.length > 0 ? n.children.map(c => nodeToHTMLElement(c)).join('') : text
  return `<${tag}${attrs}>${childrenHTML}</${tag}>`
}

function wrapInHTML(body: string): string {
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: system-ui, -apple-system, sans-serif; background: #0f0f1a; color: #fff; padding: 16px; }
    input, textarea { outline: none; font-family: inherit; }
    button { font-family: inherit; }
    img { max-width: 100%; }
  </style>
</head>
<body>
${body}
</body>
</html>`
}

function extractHTMLFromCode(code: string): string {
  // Try to extract HTML body from generated code
  const bodyMatch = code.match(/<body[^>]*>([\s\S]*?)<\/body>/)
  if (bodyMatch) return bodyMatch[1]
  // Try to find JSX return content
  const returnMatch = code.match(/return\s*\(([\s\S]*?)\)\s*;?\s*}/)
  if (returnMatch) return returnMatch[1]
  // Fallback: return as-is
  return code
}

function tabBtn(active: boolean): React.CSSProperties {
  return { padding: '4px 12px', borderRadius: 6, fontSize: 10, fontFamily: 'var(--font-mono)', fontWeight: 700, border: active ? '1px solid var(--accent)' : '1px solid var(--border-2)', background: active ? 'rgba(124,110,245,0.1)' : 'none', color: active ? 'var(--accent)' : 'var(--text-2)', cursor: 'pointer' }
}

function parseHTMLToNodes(html: string): ComponentNode[] {
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')
  const body = doc.body
  return Array.from(body.children).map(el => elementToNode(el)).filter(Boolean) as ComponentNode[]
}

function elementToNode(el: Element): ComponentNode | null {
  const tag = el.tagName.toLowerCase()
  const style = (el as HTMLElement).style
  const props: Record<string, any> = {}

  if (style.padding) props.padding = style.padding
  if (style.background || style.backgroundColor) props.background = style.background || style.backgroundColor
  if (style.color) props.color = style.color
  if (style.borderRadius) props.borderRadius = style.borderRadius
  if (style.border) props.border = style.border
  if (style.display) props.display = style.display
  if (style.gap) props.gap = style.gap
  if (style.flexDirection) props.flexDirection = style.flexDirection
  if (style.width) props.width = style.width
  if (style.height) props.height = style.height
  if (style.fontSize) props.fontSize = style.fontSize
  if (style.fontWeight) props.fontWeight = style.fontWeight
  if (style.textAlign) props.textAlign = style.textAlign
  if (style.alignItems) props.alignItems = style.alignItems
  if (style.justifyContent) props.justifyContent = style.justifyContent

  let type = 'div'
  if (tag === 'h1' || tag === 'h2' || tag === 'h3') { type = 'heading'; props.text = el.textContent ?? ''; props.fontSize = props.fontSize || '24px'; props.fontWeight = '700'; props.color = props.color || '#fff' }
  else if (tag === 'p' || tag === 'span') { type = 'text'; props.text = el.textContent ?? ''; props.fontSize = props.fontSize || '14px'; props.color = props.color || '#ccc' }
  else if (tag === 'button') { type = 'button'; props.text = el.textContent ?? 'Button'; props.background = props.background || '#7c6ef5'; props.color = props.color || '#fff'; props.borderRadius = props.borderRadius || '8px'; props.border = props.border || 'none'; props.cursor = 'pointer' }
  else if (tag === 'input') { type = 'input'; props.placeholder = el.getAttribute('placeholder') ?? 'Enter text...'; props.background = props.background || '#0f0f1a'; props.color = props.color || '#fff'; props.borderRadius = props.borderRadius || '6px'; props.border = props.border || '1px solid #333'; props.width = props.width || '100%' }
  else if (tag === 'textarea') { type = 'textarea'; props.placeholder = el.getAttribute('placeholder') ?? 'Enter text...'; props.background = props.background || '#0f0f1a'; props.color = props.color || '#fff'; props.borderRadius = props.borderRadius || '6px'; props.border = props.border || '1px solid #333'; props.width = props.width || '100%'; props.rows = parseInt(el.getAttribute('rows') ?? '4') }
  else if (tag === 'img') { type = 'image'; props.src = el.getAttribute('src') ?? 'https://placehold.co/300x200'; props.width = props.width || '100%'; props.borderRadius = props.borderRadius || '8px' }
  else if (tag === 'a') { type = 'link'; props.text = el.textContent ?? 'Link'; props.color = props.color || '#7c6ef5'; props.href = el.getAttribute('href') ?? '#' }
  else if (tag === 'form') { type = 'form'; props.display = 'flex'; props.flexDirection = 'column'; props.gap = props.gap || '12px'; props.padding = props.padding || '20px' }
  else if (tag === 'nav') { type = 'navbar'; props.display = 'flex'; props.alignItems = 'center'; props.justifyContent = 'space-between'; props.padding = props.padding || '12px 24px'; props.background = props.background || '#0f0f1a' }
  else if (tag === 'footer') { type = 'footer'; props.padding = props.padding || '24px'; props.background = props.background || '#0a0a14'; props.textAlign = 'center'; props.color = props.color || '#666' }
  else if (tag === 'section') { type = 'section'; props.padding = props.padding || '32px 24px' }
  else if (props.display === 'flex') { type = 'flex' }
  else if (props.display === 'grid') { type = 'grid' }
  else { props.padding = props.padding || '16px' }

  const children: ComponentNode[] = []
  if (el.children.length > 0 && !['heading', 'text', 'button', 'input', 'textarea', 'image', 'link', 'badge'].includes(type)) {
    for (const child of Array.from(el.children)) {
      const childNode = elementToNode(child)
      if (childNode) children.push(childNode)
    }
  }

  return { id: crypto.randomUUID(), type, props, children }
}
